import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import {
  AlignLeftIcons,
  ChartIcons,
  FileDownIcons,
  FilePlusIcons,
  MegaPhoneIcons,
  ShieldIcons,
  SlidersHorizontalIcons,
  SquareCheckIcons,
  StarIcons,
} from '../../../../icons/icons';
import { IUserDisplay } from '../../../../utils/api_service/access_control';
import { getEmployeeList } from '../../../../utils/api_service/employees';
import { searchJobTitleList } from '../../../../utils/api_service/job_title';
import { JobOpeningsContext } from '../../../../utils/contexts/JobOpening';
import { EmployeeUser, JobTitle } from '../../../../utils/types/admin_types';
import { jobOpeningDetailsTabs } from '../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { useJobOpeningNavigation } from '../Hooks/useJobOpeningNavigation';
import LoadingSpinner from '../ui/LoaderSpinner';
import { AutoAssessmentTab } from './AutoAssessmentTab';
import { AutoEvaluationTab } from './AutoEvaluationTab';
import CVIngestionTab from './CVIngestion';
import { IJobOpeningDetails } from './Interfaces/IJobOpeningDetails';
import JobDescriptionTab from './JobDescription';
import JobPosting from './JobPosting';
import CCATab from './JobPosting/CCATab';
import { JobRequisitionPage } from './JobRequisition/JobRequsitionPage';
import { Permissions } from './Permissions';

function safeJobUserToUserDisplay(jobUser: any): IUserDisplay | undefined {
  if (
    !jobUser ||
    typeof jobUser !== 'object' ||
    !jobUser.user ||
    typeof jobUser.user.firstName !== 'string'
  ) {
    return undefined;
  }
  return {
    firstName: jobUser.user.firstName,
    lastName: jobUser.user.lastName,
    email: jobUser.user.email,
    id: jobUser.user.id,
    orgUserId: jobUser.id,
    permissions: [],
  };
}

const JobOpeningDetails = (props: IJobOpeningDetails) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(NS.adminPages);
  const {
    jobOpeningElements,
    completedJobOpeningDetails,
    setCompletedJobOpeningDetails,
  } = useContext(JobOpeningsContext);
  const { currentSubTab, navigateToSubTab } = useJobOpeningNavigation();

  const [jobTitleList, setJobTitleList] = useState<JobTitle[]>([]);
  const [defaultEmployeeUserList, setDefaultEmployeeUserList] = useState<
    EmployeeUser[]
  >([]);
  const isLoading = false;

  // States to persist the labels for hiring manager and recruiter
  const [hiringManagerOptions, setHiringManagerOptions] =
    useState<SelectOptions[] | undefined>(undefined);
  const [internalRecruiterOptions, setInternalRecruiterOptions] =
    useState<SelectOptions[] | undefined>(undefined);

  const sidebarButtons = useMemo(
    () => [
      { value: 'Job Requisition' as jobOpeningDetailsTabs, label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.jobRequisition), Icon: FilePlusIcons, disabled: false },
      { value: 'Job Description' as jobOpeningDetailsTabs, label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.jobDescription), Icon: AlignLeftIcons, disabled: false },
      {
        value: 'Auto Suitability' as jobOpeningDetailsTabs,
        label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.autoSuitability),
        Icon: SquareCheckIcons,
        disabled: !jobOpeningElements.includes('Auto-Suitability'),
      },
      {
        value: 'Auto Outstanding' as jobOpeningDetailsTabs,
        label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.autoOutstanding),
        Icon: StarIcons,
        disabled: !jobOpeningElements.includes('Auto-Outstanding'),
      },
      {
        value: 'Auto Assess Questions' as jobOpeningDetailsTabs,
        label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.autoAssessQuestions),
        disabled: !jobOpeningElements.includes('Auto-Assessments'),
        Icon: SlidersHorizontalIcons,
      },
      {
        value: 'Job Posting' as jobOpeningDetailsTabs,
        label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.jobPosting),
        Icon: MegaPhoneIcons,
        disabled: !jobOpeningElements.includes('Job Posting'),
      },
      {
        value: 'CV Ingestion' as jobOpeningDetailsTabs,
        label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.cvIngestion),
        Icon: FileDownIcons,
        disabled: !jobOpeningElements.includes('Applicant Analysis'),
      },
      {
        value: 'CCA Questions' as jobOpeningDetailsTabs,
        label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.ccaQuestions),
        Icon: ChartIcons,
        disabled: !jobOpeningElements.includes('CCA' as any),
      },
      { value: 'Permissions' as jobOpeningDetailsTabs, label: t(ADMIN_PAGES_KEYS.jobOpenings.sidebar.permissions), Icon: ShieldIcons, disabled: false },
    ],
    [jobOpeningElements, t],
  );

  useEffect(() => {
    searchJobTitleList('').then(setJobTitleList);
    getEmployeeList('', 1, 100)
      .then((res) => res.json())
      .then((data) => setDefaultEmployeeUserList(data.employee));
  }, []);

  const editable = !completedJobOpeningDetails.includes('CV Ingestion');
  const completedElement = () => {
    if (!completedJobOpeningDetails.includes(currentSubTab)) {
      setCompletedJobOpeningDetails([
        ...completedJobOpeningDetails,
        currentSubTab,
      ]);
    }
  };

  const userToOption = (u: any) => {
    if (!u) return null;
    if (u.label && u.value) return u;
    if (u.user) {
      return {
        value: u.id,
        label: `${u.user.firstName} ${u.user.lastName}`,
      };
    }
    return null;
  };

  useEffect(() => {
    if (
      props.jobRequisition?.hiringManager?.length > 0 &&
      !hiringManagerOptions
    ) {
      const options = props.jobRequisition.hiringManager
        .map(userToOption)
        .filter(Boolean) as SelectOptions[];
      if (options.length > 0) setHiringManagerOptions(options);
    }
    if (
      props.jobRequisition?.internalRecruiter?.length > 0 &&
      !internalRecruiterOptions
    ) {
      const options = props.jobRequisition.internalRecruiter
        .map(userToOption)
        .filter(Boolean) as SelectOptions[];
      if (options.length > 0) setInternalRecruiterOptions(options);
    }
  }, [props.jobRequisition, hiringManagerOptions, internalRecruiterOptions]);

  const renderActiveTab = () => {
    switch (currentSubTab) {
      case 'Job Requisition':
        return (
          <JobRequisitionPage
              {...props}
              editable={editable}
              completedElement={completedElement}
              employees={defaultEmployeeUserList}
              setJobRequisition={props.setJobRequisition as any}
              setJobDescription={props.setJobDescription as any}
              defaultHiringManager={hiringManagerOptions}
              defaultInternalRecruiter={internalRecruiterOptions}
              setDefaultHiringManager={setHiringManagerOptions}
              setDefaultInternalRecruiter={setInternalRecruiterOptions}
          />
        );
      case 'Job Description':
        return (
          <JobDescriptionTab
              {...props}
              editable={editable}
              completedElement={completedElement}
              jobTitleList={jobTitleList}
              jobDescription={props.jobDescription}
              setJobDescription={props.setJobDescription as any}
              jobTitle={
                typeof props.jobDescription.jobTitle === 'object' &&
              props.jobDescription.jobTitle !== null
                  ? {
                    value:
                      (props.jobDescription.jobTitle as any).id ||
                      (props.jobDescription.jobTitle as any).value,
                    label:
                      (props.jobDescription.jobTitle as any).jobTitle ||
                      (props.jobDescription.jobTitle as any).label,
                  }
                  : {
                    value: props.jobDescription.jobTitle as any,
                    label:
                      props.jobTitleLabel ||
                      (props.jobDescription.jobTitle as any),
                  }
              }
              setJobTitle={() => {}}
              setJobTitleLabel={props.setJobTitleLabel as any}
              setAutoSuitability={props.setAutoSuitability as any}
          />
        );
      case 'CV Ingestion':
        return (
          <CVIngestionTab {...props} completedElement={completedElement} />
        );
      case 'Job Posting':
        return (
          <JobPosting
              {...props}
              jobTitleList={jobTitleList}
              setJobPostingData={props.setJobPostingData as any}
              setStages={props.setStages as any}
              jobPostingData={props.jobPostingData}
              jobDescription={props.jobDescription}
              jobPostExist={props.jobPostExist}
              autoAssessment={props.autoAssessment}
          />
        );
      case 'Auto Suitability':
        return (
          <AutoEvaluationTab
              {...props}
              editable={editable}
              completedElement={completedElement}
              evaluationType='Auto Suitability'
              autoEvaluation={props.autoSuitability}
              setAutoEvaluation={props.setAutoSuitability as any}
              jobDescription={props.jobDescription}
          />
        );
      case 'Auto Outstanding':
        return (
          <AutoEvaluationTab
              {...props}
              editable={editable}
              completedElement={completedElement}
              evaluationType='Auto Outstanding'
              autoEvaluation={props.autoOutstanding}
              setAutoEvaluation={props.setAutoOutstanding as any}
              importAutoSuitability={() =>
                props.setAutoOutstanding(props.autoSuitability)
              }
              jobDescription={props.jobDescription}
          />
        );
      case 'Auto Assess Questions':
        return (
          <AutoAssessmentTab
              {...props}
              completedElement={completedElement}
              autoAssessment={props.autoAssessment}
              setAutoAssessment={props.setAutoAssessment as any}
              jobDescription={props.jobDescription}
              jobTitle={
                typeof props.jobDescription.jobTitle === 'object'
                  ? (props.jobDescription.jobTitle as any).jobTitle
                  : props.jobTitleLabel || (props.jobDescription.jobTitle as any)
              }
              jobTitleId={
                typeof props.jobDescription.jobTitle === 'object'
                  ? (props.jobDescription.jobTitle as any).id
                  : (props.jobDescription.jobTitle as any)
              }
          />
        );
      case 'Permissions':
        return (
          <Permissions
              resourceId={id}
              resourceType='Job-Opening'
              employees={defaultEmployeeUserList}
              defaultHiringManager={
              props.jobRequisition.hiringManager
                ?.map(safeJobUserToUserDisplay)
                .filter(Boolean) as any
              }
              defaultInternalRecruiter={
              props.jobRequisition.internalRecruiter
                ?.map(safeJobUserToUserDisplay)
                .filter(Boolean) as any
              }
          />
        );
      case 'CCA Questions':
        return <CCATab setActiveTab={() => {}} />;
      default:
        // Default to Job Requisition if sub-tab doesn't match or is undefined
        return (
          <JobRequisitionPage
              {...props}
              editable={editable}
              completedElement={completedElement}
              employees={defaultEmployeeUserList}
              setJobRequisition={props.setJobRequisition as any}
              setJobDescription={props.setJobDescription as any}
              defaultHiringManager={hiringManagerOptions}
              defaultInternalRecruiter={internalRecruiterOptions}
              setDefaultHiringManager={setHiringManagerOptions}
              setDefaultInternalRecruiter={setInternalRecruiterOptions}
          />
        );
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className='flex min-w-0 flex-col gap-2.5 lg:flex-row'>
      { /* Sidebar Navigation */ }
      <div className='flex w-full gap-1.5 overflow-x-auto rounded-lg border border-slate-100 bg-slate-50/70 p-1.5 lg:sticky lg:top-4 lg:h-fit lg:w-16 lg:shrink-0 lg:flex-col lg:gap-2 lg:overflow-visible lg:rounded-2xl lg:py-3'>
        { sidebarButtons.map((button, index) => {
          if (button.disabled) return null;
          const isActive = currentSubTab === button.value;
          const Icon = button.Icon;
          return (
            <button
                key={index}
                onClick={() =>
                  navigateToSubTab(button.value)
                }
                className='group flex min-w-fit flex-row items-center gap-1.5 px-1 transition-all duration-200 lg:min-w-0 lg:flex-col lg:gap-0 lg:px-0'
            >
              <div
                  className={`w-7 h-7 rounded-lg flex shrink-0 items-center justify-center transition-all duration-300 border lg:w-9 lg:h-9 lg:rounded-xl ${
                    isActive
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-100 lg:scale-105'
                      : 'bg-white border-slate-200 text-slate-400 hover:border-emerald-400 hover:text-emerald-600 group-hover:shadow-md'
                  }`}
              >
                <Icon
                    className={`w-3.5 h-3.5 lg:w-4 lg:h-4 ${
                      isActive ? 'text-white' : 'text-slate-400'
                    }`}
                />
              </div>
              <p
                  className={`text-[9px] font-bold uppercase tracking-tight text-left leading-tight transition-colors lg:mt-1 lg:text-center lg:text-[7.5px] ${
                    isActive
                      ? 'text-emerald-600'
                      : 'text-slate-400 group-hover:text-emerald-500'
                  }`}
              >
                { button.label }
              </p>
            </button>
          );
        }) }
      </div>

      { /* Main Content Area */ }
      <div
          className={`
        w-full min-w-0
        ${currentSubTab === 'Job Posting' ? 'p-1' : 'p-2.5 sm:p-3'}
        box-border
        overflow-y-auto 
        bg-white
        rounded-lg
        shadow-sm
        min-h-[360px]
      `}
          style={{
            maxHeight: 'calc(100dvh - 220px)',
          }}
      >
        { renderActiveTab() }
      </div>
    </div>
  );
};

export default JobOpeningDetails;
